import type { CampaignStatistics } from '@hprofits/api';
import type { SubTargetPricingRuleOutput } from '@hprofits/api/ad-group/types';
import type { AdGroupId, TargetGroupId } from '@hprofits/shared';
import type { ComputedRef } from 'vue';
import { computed } from 'vue';

import { useCampaignGroupContext } from '@/modules/campaigns/composables/context';

export type TargetConflictFlags = {
  /** Ad groups holding at least one sub-rule its target group blocks. */
  adGroupIds: ReadonlySet<AdGroupId>;
  /** Target groups blocking at least one sub-rule of an ad group that uses them. */
  targetGroupIds: ReadonlySet<TargetGroupId>;
};

const hasConflict = (subRules: ReadonlyArray<SubTargetPricingRuleOutput>): boolean =>
  subRules.some((subRule) => subRule.targetConflict || hasConflict(subRule.subRules ?? []));

/**
 * Which sidebar entities carry a target conflict, derived from the same campaign
 * statistics the pricing-rule lock reads — the response already carries every ad group's
 * full rule tree with the server-derived `targetConflict`, so the sidebar needs no
 * endpoint of its own.
 *
 * Both sides are flagged on purpose: the ad group is where the unbuyable rule lives, the
 * target group is what made it unbuyable.
 */
export const useTargetConflictFlags = (): ComputedRef<TargetConflictFlags> => {
  const { campaignGroupStatisticsSource } = useCampaignGroupContext();

  return computed(() => {
    const adGroupIds = new Set<AdGroupId>();
    const targetGroupIds = new Set<TargetGroupId>();

    const statistics: CampaignStatistics | null | undefined = campaignGroupStatisticsSource.state.data.value;
    for (const adGroup of statistics?.adGroups ?? []) {
      for (const rule of adGroup.pricingRules) {
        if (hasConflict(rule.subRules)) {
          adGroupIds.add(adGroup.id);
          targetGroupIds.add(rule.targetGroupId);
        }
      }
    }

    return { adGroupIds, targetGroupIds };
  });
};
